import { useSearchParams } from 'react-router-dom'
import { useEffect } from 'react'
import { trackAnalyticsTabViewed } from '@/lib/analytics-events'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { TrendsTab } from './TrendsTab'
import { CompareTab } from './CompareTab'
import { DecideTab } from '@/components/analytics/DecideTab'
import { AnalyticsAboutDrawer } from '@/components/analytics/AnalyticsAboutDrawer'
import type { TimeRange } from '@/types/story'

const RANGES: TimeRange[] = ['7d', '30d', '90d']
const VALID_TABS = ['decide', 'trends', 'compare'] as const
type Tab = (typeof VALID_TABS)[number]

// Legacy URL backfill: previously the default tab was `overview` (now
// renamed to `trends`) and the page had no `decide` tab. With the P8a
// rebuild, Decide is the default; an inbound `?tab=overview` is
// rewritten to `decide` so old bookmarks land on the new default.
function parseTab(raw: string | null): Tab {
  if (raw === 'overview') return 'decide'
  return (VALID_TABS as readonly string[]).includes(raw ?? '')
    ? (raw as Tab)
    : 'decide'
}

function parseRange(raw: string | null): TimeRange {
  return (RANGES as readonly string[]).includes(raw ?? '')
    ? (raw as TimeRange)
    : '30d'
}

/**
 * /analytics route shell — owns `?tab=` and `?range=` URL params and renders
 * the shadcn Tabs container around three panels: Decide (default — decision
 * support), Trends (the existing StoriesAnalyticsPage surface) and Compare
 * (cross-sector analytics).
 *
 * Range state is shared by Trends + Compare; Decide tab is range-agnostic
 * (its panels use spec-locked 7d windows internally).
 */
export function AnalyticsPageShell() {
  const [searchParams, setSearchParams] = useSearchParams()
  const tab = parseTab(searchParams.get('tab'))
  const range = parseRange(searchParams.get('range'))

  // KAN-32 analytics:tab_viewed — keyed on `tab` ONLY. The shell re-renders on
  // range changes too, which must not emit a tab view.
  useEffect(() => {
    trackAnalyticsTabViewed(tab)
  }, [tab])

  const setTab = (t: Tab) => {
    const next = new URLSearchParams(searchParams)
    next.set('tab', t)
    setSearchParams(next, { replace: true })
  }

  const setRange = (r: TimeRange) => {
    const next = new URLSearchParams(searchParams)
    next.set('range', r)
    setSearchParams(next, { replace: true })
  }

  return (
    <main className="mx-auto flex max-w-7xl flex-col gap-6 p-4 md:p-6">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Analytics</h1>
          <p className="mt-0.5 text-sm text-muted-foreground">
            Decision support, coverage trends, and cross-sector comparisons.
          </p>
        </div>
        <AnalyticsAboutDrawer />
      </header>

      <Tabs value={tab} onValueChange={(v) => setTab(v as Tab)}>
        <TabsList>
          <TabsTrigger value="decide">Decide</TabsTrigger>
          <TabsTrigger value="trends">Trends</TabsTrigger>
          <TabsTrigger value="compare">Compare</TabsTrigger>
        </TabsList>
        <TabsContent value="decide" className="mt-4">
          <DecideTab />
        </TabsContent>
        <TabsContent value="trends" className="mt-4">
          <TrendsTab range={range} setRange={setRange} />
        </TabsContent>
        <TabsContent value="compare" className="mt-4">
          <CompareTab range={range} />
        </TabsContent>
      </Tabs>
    </main>
  )
}
